import type {
  CanvasBoundsType,
  CombinedResults,
  HandsControlData,
  MediapipeHandResults,
  MediapipePoseResults,
  PoseAngles,
  PoseControlData,
} from "/@/shared/motion-menu-system/MenuTypes";

import { getHashParamFromWindow } from "@metapages/hash-query";

import { CameraManager } from "./camera/CameraManager.ts";
import { getHandsVisible } from "./control/common.ts";
import { renderDebugClench } from "./control/hands-clench.ts";
import { generateHandsControl } from "./control/hands.ts";
import { HandTokenizerController } from "./control/HandTokenizer.ts";
import { generatePoseControl } from "./control/pose-and-hands.ts";
import { calculatePoseAngles } from "./control/pose-angles.ts";
import { HandsFreeTranscriber } from "./control/transcription.ts";
import {
  generateRotationController,
  renderRotationControl,
  RotationController,
} from "./controllers/3dRotationControl.ts";
import {
  DirectionalControllerGlyphController,
} from "./controllers/DirectionalControllerGlyph.ts";
// import {
//   DirectionalControllerPrototype01Controller,
// } from './controllers/DirectionalControllerPrototype01.ts';
// import {
//   DirectionalControllerPrototype02Controller,
// } from './controllers/DirectionalControllerPrototype02.ts';
import {
  GrabTwistController,
} from "./controllers/grab-twist-left-right-controller.ts";
import {
  generateScalarController,
  renderScalarControl,
  ScalarController,
} from "./controllers/scalarControl.ts";
import {
  TwoHandGrabOpenCloseController,
} from "./controllers/TwoHandGrabOpenCloseOptions.ts";
import { IIOStream, MotionController } from "./controllers/types.ts";
import { SegmentationManager } from "./detection/SegmentationManager.ts";
import { VisionDetector } from "./detection/VisionDetector.ts";
import { OptionsManager } from "./options/OptionsManager.ts";

// 🚀 overlayv1-script module executing — each appearance = one full script load.
// If you see this multiple times it means the iframe or script is being reloaded.
// Each instance gets a short ID so camera logs from different instances are distinguishable.
const _instanceId = Math.random().toString(36).slice(2, 6).toUpperCase();
console.log(
  `🚀 [camera-instance:${_instanceId}] overlayv1-script executing @ ${
    new Date().toISOString()
  } (reload #${((globalThis as any).__overlayLoadCount =
    ((globalThis as any).__overlayLoadCount ?? 0) + 1)})`,
);

// ===== CLEANUP PREVIOUS MODULE INSTANCES =====
// On module re-execution (HMR, iframe lifecycle), dispose previous WebGL-holding
// instances to prevent "Too many active WebGL contexts" browser warnings.
{
  const prev = (globalThis as any).__overlayCleanup as {
    cameraManager?: { cleanup(): void };
    visionDetector?: { dispose(): void };
    segmentation?: { dispose(): void };
    controllers?: { dispose(): void }[];
  } | undefined;
  if (prev) {
    console.log(
      `[camera-instance:${_instanceId}] Disposing previous camera/module instances`,
    );
    try {
      prev.cameraManager?.cleanup();
    } catch (_) { /* best-effort */ }
    try {
      prev.visionDetector?.dispose();
    } catch (_) { /* best-effort */ }
    try {
      prev.segmentation?.dispose();
    } catch (_) { /* best-effort */ }
    prev.controllers?.forEach((c) => {
      try {
        c.dispose();
      } catch (_) { /* best-effort */ }
    });
  }
}
const __overlayCleanup: {
  cameraManager?: { cleanup(): void };
  visionDetector?: { dispose(): void };
  segmentation?: { dispose(): void };
  controllers: { dispose(): void }[];
} = { controllers: [] };
(globalThis as any).__overlayCleanup = __overlayCleanup;

// ===== IOStream: per-frame output accumulator =====
// Collects all setOutput/setOutputs calls during a frame sweep and emits
// a single merged metaframe.setOutputs at the end of each frame (flush).
class IOStream implements IIOStream {
  private pending: Record<string, unknown> = {};

  get inputs(): Record<string, unknown> {
    return globalThis.metaframe!.getInputs();
  }
  readonly setOutput = (key: string, value: unknown): void => {
    this.pending[key] = value;
  };
  readonly setOutputs = (outputs: Record<string, unknown>): void => {
    Object.assign(this.pending, outputs);
  };
  readonly onInputs = (
    listener: Parameters<typeof globalThis.metaframe.onInputs>[0],
  ): ReturnType<typeof globalThis.metaframe.onInputs> => {
    setTimeout(() => {
      listener(this.inputs);
    }, 0);
    return globalThis.metaframe!.onInputs(listener);
  };
  flush(): void {
    if (Object.keys(this.pending).length > 0) {
      globalThis.metaframe!.setOutputs(this.pending);
      this.pending = {};
    }
  }
  reset(): void {
    this.pending = {};
  }
}
const iostream = new IOStream();

// listen to options changes
console.assert(!!globalThis.metaframe, "metaframe is not defined");

// Listen for menu navigation data from browser
globalThis.metaframe?.onInputs((inputs: Record<string, unknown>) => {
  if (inputs?.menuNavData) {
    globalThis.menuNavData = inputs
      .menuNavData as typeof globalThis.menuNavData;
  }
});

const transcriber = new HandsFreeTranscriber();

let glyphLeftRightController: DirectionalControllerGlyphController | undefined;
let glyphForwardBackwardController:
  | DirectionalControllerGlyphController
  | undefined;
// let proto01LeftRightController:
//   | DirectionalControllerPrototype01Controller
//   | undefined;
// let proto01ForwardBackwardController:
//   | DirectionalControllerPrototype01Controller
//   | undefined;
// let proto02LeftRightController:
//   | DirectionalControllerPrototype02Controller
//   | undefined;
// let proto02ForwardBackwardController:
//   | DirectionalControllerPrototype02Controller
//   | undefined;
let grabTwistLeftRightController: GrabTwistController | undefined;
let grabTwistForwardBackwardController: GrabTwistController | undefined;
let twoHandGrabOpenCloseController: MotionController | undefined;
let flickHandLeftRightController: MotionController | undefined;
let menuNavigationController: MotionController | undefined;
let handTokenizerController: MotionController | undefined;

const visionDetector = new VisionDetector();
__overlayCleanup.visionDetector = visionDetector;

const calculateHandsControl = generateHandsControl();
const calculatePoseControl = generatePoseControl();

// Only create scalarController if enabled in options (default: disabled)
let scalarController: ScalarController | null = null;

// Only create rotationController if enabled in options (default: disabled)
let rotationController: RotationController | null = null;

// Get DOM elements
const videoElement = document.getElementById("webcam") as HTMLVideoElement;
const canvasElement = document.getElementById(
  "output-canvas",
) as HTMLCanvasElement;
// Enable alpha channel for transparency support (critical for iframe embedding)
const canvasCtx = canvasElement.getContext("2d", { alpha: true })!;
const controllerOverlay = document.getElementById(
  "controller-overlay",
) as HTMLDivElement;

if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
  throw new Error(
    "Browser API navigator.mediaDevices.getUserMedia not available",
  );
}

// iOS Safari viewport height fix
// Sets a CSS custom property --vh that represents 1% of the actual viewport height
// This fixes the 100vh bug where iOS Safari doesn't account for the browser chrome
function setViewportHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
}

// Set initial viewport height
setViewportHeight();

// Track last known orientation for change detection
let lastOrientation = window.screen?.orientation?.angle ?? window.orientation ??
  0;

// ===== DIAGNOSTIC LOGGING =====
const _moduleLoadCount = ((globalThis as any).__overlayModuleLoadCount ?? 0) +
  1;
(globalThis as any).__overlayModuleLoadCount = _moduleLoadCount;
// Use sessionStorage to track reloads (survives page reloads within same tab)
const _sessionReloadCount = parseInt(
  sessionStorage.getItem("__overlayReloadCount") ?? "0",
) + 1;
sessionStorage.setItem("__overlayReloadCount", String(_sessionReloadCount));
// Check navigation type: "reload", "navigate", "back_forward", or "prerender"
const _navType = performance.getEntriesByType?.("navigation")?.[0] as
  | PerformanceNavigationTiming
  | undefined;
console.log(
  `🔄 overlayv1-script.ts MODULE LOADED #${_moduleLoadCount} (session reload #${_sessionReloadCount})`,
  {
    navigationType: _navType?.type ?? "unknown",
    locationHash: globalThis.location.hash.substring(0, 80),
  },
);
if (_moduleLoadCount > 1) {
  console.warn(
    `⚠️ Module re-executed ${_moduleLoadCount} times in same page context!`,
    new Error("Module re-execution stack trace"),
  );
}
if (_sessionReloadCount > 1) {
  console.warn(
    `⚠️ Page reloaded ${_sessionReloadCount} times in this tab session (navigationType: ${
      _navType?.type ?? "unknown"
    })`,
  );
}
const diagSessionStart = performance.now();
const getDiagTime = () =>
  ((performance.now() - diagSessionStart) / 1000).toFixed(2);
let lastResizeTrigger = "init";
let lastFlipState: boolean | undefined;
let lastScreenCoordsHash = "";

// Update on resize and orientation change (important for iOS)
window.addEventListener("resize", setViewportHeight);
window.addEventListener("orientationchange", () => {
  // Delay to allow iOS to finish orientation transition
  setTimeout(setViewportHeight, 100);
});

// Performance metrics tracking - must be defined before model initialization
interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  handDetectionTime: number;
  poseDetectionTime: number;
  postProcessingTime: number; // Time for clench, angles, controls calculations
  segmentationTime: number;
  renderTime: number;
  totalComputeTime: number;
  outputSize: number;
  modelLoadCount: number;
  modelUnloadCount: number;
  handDetectionStateChanges: number;
  lastHandCount: number;
  currentHandCount: number;
  transformAppliedCount: number;
  transformSkippedCount: number;
  lastTransformState: boolean;
  canvasBoundsChanges: number;
  lastCanvasBoundsHash: string;
  memoryMB: number;
  segWorkerLatencyMs: number;
  iframeCount: number;
  frameHistory: number[];
  detectionHistory: {
    hand: number[];
    pose: number[];
    postProcessing: number[]; // History for averaging post-processing time
    segmentation: number[];
  };
}

// Debug overlay scale factor - adjust this to change debug text size
// 1.0 = default scaling based on viewport, increase for larger text
const DEBUG_SCALE_FACTOR = 2.0;

const perfMetrics: PerformanceMetrics = {
  fps: 0,
  frameTime: 0,
  handDetectionTime: 0,
  poseDetectionTime: 0,
  postProcessingTime: 0,
  segmentationTime: 0,
  renderTime: 0,
  totalComputeTime: 0,
  outputSize: 0,
  modelLoadCount: 0,
  modelUnloadCount: 0,
  handDetectionStateChanges: 0,
  lastHandCount: 0,
  currentHandCount: 0,
  transformAppliedCount: 0,
  transformSkippedCount: 0,
  lastTransformState: false,
  canvasBoundsChanges: 0,
  lastCanvasBoundsHash: "",
  memoryMB: 0,
  segWorkerLatencyMs: 0,
  iframeCount: 0,
  frameHistory: [],
  detectionHistory: {
    hand: [],
    pose: [],
    postProcessing: [],
    segmentation: [],
  },
};

// Track last 60 frames for averaging
const FRAME_HISTORY_SIZE = 60;

function updateFrameHistory(time: number) {
  perfMetrics.frameHistory.push(time);
  if (perfMetrics.frameHistory.length > FRAME_HISTORY_SIZE) {
    perfMetrics.frameHistory.shift();
  }
}

function updateDetectionHistory(
  type: "hand" | "pose" | "postProcessing" | "segmentation",
  time: number,
) {
  perfMetrics.detectionHistory[type].push(time);
  if (perfMetrics.detectionHistory[type].length > FRAME_HISTORY_SIZE) {
    perfMetrics.detectionHistory[type].shift();
  }
}

function calculateAverage(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

// Poll resource metrics once per second (memory + iframe count).
setInterval(() => {
  const mem = (performance as any).memory;
  if (mem) {
    perfMetrics.memoryMB = mem.usedJSHeapSize / 1048576;
  }
  try {
    perfMetrics.iframeCount = window.parent?.frames?.length ?? 0;
  } catch {
    perfMetrics.iframeCount = -1;
  }
}, 1000);

function renderDebugOverlay() {
  if (!globalThis.options?.debug) return;

  const avgFrameTime = calculateAverage(perfMetrics.frameHistory);
  const avgHandTime = calculateAverage(perfMetrics.detectionHistory.hand);
  const avgPoseTime = calculateAverage(perfMetrics.detectionHistory.pose);
  const avgPostProcessingTime = calculateAverage(
    perfMetrics.detectionHistory.postProcessing,
  );
  const avgSegTime = calculateAverage(
    perfMetrics.detectionHistory.segmentation,
  );

  // Calculate scale based on viewport width (baseline: 1920px width = scale 1.0)
  // This makes the debug overlay responsive to screen size
  const baseWidth = 1920;
  const viewportScale = Math.max(
    0.5,
    Math.min(2.0, window.innerWidth / baseWidth),
  );
  const scale = viewportScale * DEBUG_SCALE_FACTOR;

  // Background for better readability - light grey, mostly transparent
  const padding = 20 * scale;
  const boxWidth = 550 * scale;
  const boxHeight = 870 * scale; // Expanded for Resources section
  canvasCtx.fillStyle = "rgba(200, 200, 200, 0.15)";
  canvasCtx.fillRect(padding, padding, boxWidth, boxHeight);

  // Text styling - scaled based on viewport
  canvasCtx.textBaseline = "top";

  let y = padding + 15 * scale;
  const lineHeight = 28 * scale;
  const sectionSpacing = 15 * scale;
  const leftMargin = padding + 15 * scale;

  // FPS and frame time - highlight these as they're most important
  canvasCtx.font = `bold ${24 * scale}px monospace`;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(`FPS: ${perfMetrics.fps.toFixed(1)}`, leftMargin, y);
  y += lineHeight + 5 * scale;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillText(`Frame Time: ${avgFrameTime.toFixed(2)}ms`, leftMargin, y);
  y += lineHeight;

  // Computation breakdown
  y += sectionSpacing;
  canvasCtx.fillStyle = "#ffdd00";
  canvasCtx.font = `bold ${22 * scale}px monospace`;
  canvasCtx.fillText("=== Timing Breakdown ===", leftMargin, y);
  y += lineHeight;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(
    `Hand Detection: ${avgHandTime.toFixed(2)}ms`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Pose Detection: ${avgPoseTime.toFixed(2)}ms`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Post-Processing: ${avgPostProcessingTime.toFixed(2)}ms`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(`Segmentation: ${avgSegTime.toFixed(2)}ms`, leftMargin, y);
  y += lineHeight;
  canvasCtx.fillText(
    `Render Time: ${perfMetrics.renderTime.toFixed(2)}ms`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Total Compute: ${perfMetrics.totalComputeTime.toFixed(2)}ms`,
    leftMargin,
    y,
  );
  y += lineHeight;

  // Model stats
  y += sectionSpacing;
  canvasCtx.fillStyle = "#ffdd00";
  canvasCtx.font = `bold ${22 * scale}px monospace`;
  canvasCtx.fillText("=== Model Stats ===", leftMargin, y);
  y += lineHeight;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(
    `Model Loads: ${perfMetrics.modelLoadCount}`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Model Unloads: ${perfMetrics.modelUnloadCount}`,
    leftMargin,
    y,
  );
  y += lineHeight;

  // Output stats
  y += sectionSpacing;
  canvasCtx.fillStyle = "#ffdd00";
  canvasCtx.font = `bold ${22 * scale}px monospace`;
  canvasCtx.fillText("=== Output Stats ===", leftMargin, y);
  y += lineHeight;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(
    `Output Size: ${(perfMetrics.outputSize / 1024).toFixed(2)}KB`,
    leftMargin,
    y,
  );
  y += lineHeight;

  // Hand detection diagnostics
  y += sectionSpacing;
  canvasCtx.fillStyle = "#ffdd00";
  canvasCtx.font = `bold ${22 * scale}px monospace`;
  canvasCtx.fillText("=== Hand Detection ===", leftMargin, y);
  y += lineHeight;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(
    `Current Hands: ${perfMetrics.currentHandCount}`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Detection Changes: ${perfMetrics.handDetectionStateChanges}`,
    leftMargin,
    y,
  );
  y += lineHeight;

  // Transform diagnostics
  y += sectionSpacing;
  canvasCtx.fillStyle = "#ffdd00";
  canvasCtx.font = `bold ${22 * scale}px monospace`;
  canvasCtx.fillText("=== Transforms ===", leftMargin, y);
  y += lineHeight;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillStyle = perfMetrics.lastTransformState ? "#00ff00" : "#ff3333";
  canvasCtx.fillText(
    `State: ${perfMetrics.lastTransformState ? "ACTIVE" : "INACTIVE"}`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(
    `Applied: ${perfMetrics.transformAppliedCount}`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Skipped: ${perfMetrics.transformSkippedCount}`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Canvas Changes: ${perfMetrics.canvasBoundsChanges}`,
    leftMargin,
    y,
  );

  // Resources
  y += lineHeight + sectionSpacing;
  canvasCtx.fillStyle = "#ffdd00";
  canvasCtx.font = `bold ${22 * scale}px monospace`;
  canvasCtx.fillText("=== Resources ===", leftMargin, y);
  y += lineHeight;
  canvasCtx.font = `bold ${20 * scale}px monospace`;
  canvasCtx.fillStyle = "#00ff00";
  canvasCtx.fillText(
    `JS Heap: ${
      perfMetrics.memoryMB > 0 ? perfMetrics.memoryMB.toFixed(1) + "MB" : "n/a"
    }`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Iframes: ${
      perfMetrics.iframeCount >= 0 ? perfMetrics.iframeCount : "x-origin"
    }`,
    leftMargin,
    y,
  );
  y += lineHeight;
  canvasCtx.fillText(
    `Seg Worker RTT: ${perfMetrics.segWorkerLatencyMs.toFixed(1)}ms`,
    leftMargin,
    y,
  );
}

// Initialize the unified vision worker (all three models in one worker).
console.log(`[OFFSET-DIAG] Initializing models @${getDiagTime()}s`);
await visionDetector.init();
perfMetrics.modelLoadCount += 3; // hand + pose + segmentation
console.log(
  `[OFFSET-DIAG] Models initialized @${getDiagTime()}s modelLoadCount=${perfMetrics.modelLoadCount}`,
);

function transformHandLandmarksWithPose(
  handLandmarks: { x: number; y: number; z: number }[][],
  poseLandmarks: { x: number; y: number; z: number }[][],
  handednesses: {
    categoryName: string;
    displayName: string;
    index: number;
    score: number;
  }[][],
): any[] {
  if (!poseLandmarks || poseLandmarks.length === 0) {
    return handLandmarks;
  }

  const pose = poseLandmarks[0];

  // Get wrist landmarks for transformation reference
  const leftWrist = pose[15]; // Left wrist
  const rightWrist = pose[16]; // Right wrist

  // Calculate wrist distance for scaling
  const wristDistance = Math.abs(leftWrist.x - rightWrist.x);

  // Transform hand landmarks based on pose wrist positions
  return handLandmarks.map(
    (handSet: { x: number; y: number; z: number }[], handIndex: number) => {
      // Determine which wrist to use based on handedness information
      let targetWrist = leftWrist; // default to left

      if (
        handednesses &&
        handednesses[handIndex] &&
        handednesses[handIndex].length > 0
      ) {
        // Use MediaPipe's handedness detection
        const handedness = handednesses[handIndex][0].categoryName;
        // MediaPipe reports handedness from the person's perspective, but the video is mirrored
        // So "Left" hand should go to the left wrist landmark (pose[15])
        targetWrist = handedness === "Left" ? leftWrist : rightWrist;
      } else {
        // Fallback: determine by proximity to pose wrists
        const handWrist = handSet[0];
        const distToLeft = Math.abs(handWrist.x - leftWrist.x) +
          Math.abs(handWrist.y - leftWrist.y);
        const distToRight = Math.abs(handWrist.x - rightWrist.x) +
          Math.abs(handWrist.y - rightWrist.y);
        targetWrist = distToLeft < distToRight ? leftWrist : rightWrist;
      }

      // Get the hand wrist landmark (index 0) as reference point
      const handWrist: { x: number; y: number; z: number } = handSet[0];

      // Calculate the scale factor based on wrist distance
      // Using a larger scale factor (1.2) to make hands more visible
      const scaleFactor = wristDistance * 1.2;

      return handSet.map((landmark: { x: number; y: number; z: number }) => {
        // Calculate the offset from hand wrist
        const offsetX = landmark.x - handWrist.x;
        const offsetY = landmark.y - handWrist.y;

        // Apply scaling and position relative to pose wrist
        return {
          x: targetWrist.x + offsetX * scaleFactor,
          y: targetWrist.y + offsetY * scaleFactor,
          z: landmark.z * scaleFactor, // Scale z coordinate as well
        };
      });
    },
  );
}

function transformHandWorldLandmarksWithPose(
  handWorldLandmarks: { x: number; y: number; z: number }[][],
  poseWorldLandmarks: { x: number; y: number; z: number }[][],
  handednesses: {
    categoryName: string;
    displayName: string;
    index: number;
    score: number;
  }[][],
): any[] {
  if (!poseWorldLandmarks || poseWorldLandmarks.length === 0) {
    return handWorldLandmarks;
  }

  const poseWorld = poseWorldLandmarks[0];

  // Get wrist world landmarks for transformation reference
  const leftWristWorld = poseWorld[15]; // Left wrist
  const rightWristWorld = poseWorld[16]; // Right wrist

  // Calculate wrist distance for scaling in world space
  const wristDistanceWorld = Math.sqrt(
    Math.pow(leftWristWorld.x - rightWristWorld.x, 2) +
      Math.pow(leftWristWorld.y - rightWristWorld.y, 2) +
      Math.pow(leftWristWorld.z - rightWristWorld.z, 2),
  );

  // Transform hand world landmarks based on pose world wrist positions
  return handWorldLandmarks.map(
    (
      handWorldSet: { x: number; y: number; z: number }[],
      handIndex: number,
    ) => {
      // Determine which wrist to use based on handedness information
      let targetWristWorld = leftWristWorld; // default to left

      if (
        handednesses &&
        handednesses[handIndex] &&
        handednesses[handIndex].length > 0
      ) {
        // Use MediaPipe's handedness detection
        const handedness = handednesses[handIndex][0].categoryName;
        // MediaPipe reports handedness from the person's perspective, but the video is mirrored
        // So "Left" hand should go to the left wrist landmark (pose[15])
        targetWristWorld = handedness === "Left"
          ? leftWristWorld
          : rightWristWorld;
      } else {
        // Fallback: determine by proximity to pose wrists in world space
        const handWristWorld = handWorldSet[0];
        const distToLeftWorld = Math.sqrt(
          Math.pow(handWristWorld.x - leftWristWorld.x, 2) +
            Math.pow(handWristWorld.y - leftWristWorld.y, 2) +
            Math.pow(handWristWorld.z - leftWristWorld.z, 2),
        );
        const distToRightWorld = Math.sqrt(
          Math.pow(handWristWorld.x - rightWristWorld.x, 2) +
            Math.pow(handWristWorld.y - rightWristWorld.y, 2) +
            Math.pow(handWristWorld.z - rightWristWorld.z, 2),
        );
        targetWristWorld = distToLeftWorld < distToRightWorld
          ? leftWristWorld
          : rightWristWorld;
      }

      // Get the hand wrist world landmark (index 0) as reference point
      const handWristWorld: { x: number; y: number; z: number } =
        handWorldSet[0];

      // Calculate the scale factor based on wrist distance in world space
      // Using a scale factor to align hand size with pose proportions
      const scaleFactor = wristDistanceWorld * 0.8;

      return handWorldSet.map(
        (landmark: { x: number; y: number; z: number }) => {
          // Calculate the offset from hand wrist in world space
          const offsetX = landmark.x - handWristWorld.x;
          const offsetY = landmark.y - handWristWorld.y;
          const offsetZ = landmark.z - handWristWorld.z;

          // Apply scaling and position relative to pose wrist in world space
          return {
            x: targetWristWorld.x + offsetX * scaleFactor,
            y: targetWristWorld.y + offsetY * scaleFactor,
            z: targetWristWorld.z + offsetZ * scaleFactor,
          };
        },
      );
    },
  );
}

let lastVideoTime = -1;
let handResults: MediapipeHandResults | undefined;
let scalarResults: any = undefined;
let rotationResults: any = undefined;
let poseResults: MediapipePoseResults | undefined = undefined;
// let stabilizedPoseResults: any = undefined;
// let stabilizedPose: any = undefined;
let handsVisible: any = undefined;
let canvasBounds: CanvasBoundsType | undefined;
let handsControl: HandsControlData | undefined = undefined;
let poseControl: PoseControlData | undefined = undefined;

// Jitter compensation system
//
// This system provides high-level jitter compensation for all output data, including:
// - clench data (most noticeable jitter)
// - hand angles, rotations, and positions
// - pose angles and positions
// - All numeric values in the output
//
// Configuration:
// - options.jitterCompensation: Smoothing factor (0.0 = no smoothing, 1.0 = maximum smoothing)
//   Higher values reduce jitter but increase latency. Recommended: 0.3-0.7
// - options.jitterMissingDataTolerance: Number of frames to keep using last known good value
//   when data is missing (e.g., model falling behind or lighting issues). Default: 5 frames
//
// The compensation is applied at the topmost level before outputting, so all downstream
// handlers automatically receive smoothed data without needing individual implementations.
interface JitterCompensationState {
  lastGoodResult: any | null;
  smoothingFactor: number;
  missingDataTolerance: number; // frames to keep using last good value when data is missing
  missingDataCounter: number;
}

const jitterState: JitterCompensationState = {
  lastGoodResult: null,
  smoothingFactor: 0.0, // Will be updated from options
  missingDataTolerance: 5, // Keep last good value for 5 frames when data is missing
  missingDataCounter: 0,
};

// Deep clone an object
function deepClone(obj: any): any {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj);
  if (Array.isArray(obj)) return obj.map(deepClone);
  const cloned: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
}

// Check if a value is "missing" (null, undefined, or represents missing data)
function isMissing(value: any): boolean {
  return value === null || value === undefined;
}

// Check if a value is numeric (for smoothing)
function isNumeric(value: any): boolean {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

// Smooth numeric values recursively through an object
function smoothNumericValues<T>(
  current: T,
  previous: T,
  smoothingFactor: number,
): T {
  // If current is missing, return previous (if it exists)
  if (isMissing(current)) {
    return previous;
  }

  // If previous is missing, return current
  if (isMissing(previous)) {
    return current;
  }

  // Handle arrays
  if (Array.isArray(current)) {
    if (!Array.isArray(previous)) {
      return current;
    }
    // Handle arrays of different lengths - use the shorter length
    const maxLength = Math.min(current.length, previous.length);
    const smoothed = current
      .slice(0, maxLength)
      .map((item, index) =>
        smoothNumericValues(item, previous[index], smoothingFactor)
      );
    // If current is longer, append remaining items
    if (current.length > maxLength) {
      smoothed.push(...current.slice(maxLength));
    }
    return smoothed as T;
  }

  // Handle objects
  if (typeof current === "object" && typeof previous === "object") {
    const smoothed: any = {};
    const allKeys = new Set([
      ...Object.keys(current as object),
      ...Object.keys(previous as object),
    ]);

    for (const key of allKeys) {
      const currentVal = (current as Record<string, any>)[key];
      const previousVal = (previous as Record<string, any>)[key];

      // Skip timestamp and canvasBounds (don't smooth these)
      if (key === "timestamp" || key === "canvasBounds") {
        smoothed[key] = currentVal !== undefined ? currentVal : previousVal;
        continue;
      }

      smoothed[key] = smoothNumericValues(
        currentVal,
        previousVal,
        smoothingFactor,
      );
    }
    return smoothed;
  }

  // Handle boolean values - don't smooth, use current
  if (typeof current === "boolean" || typeof previous === "boolean") {
    return current;
  }

  // Handle numeric values - apply smoothing
  if (isNumeric(current) && isNumeric(previous)) {
    // Exponential moving average: newValue = previous + (current - previous) * (1 - smoothingFactor)
    // smoothingFactor = 0: no smoothing (use current value)
    // smoothingFactor = 1: maximum smoothing (keep previous value)
    return ((previous as number) +
      ((current as number) - (previous as number)) *
        (1 - smoothingFactor)) as T;
  }

  // For non-numeric values (strings, etc.), prefer current if it exists, otherwise previous
  return !isMissing(current) ? current : previous;
}

// Apply jitter compensation to results
function applyJitterCompensation(results: CombinedResults): CombinedResults {
  // Access optional properties via type-safe method
  const options = globalThis.options as any;
  const smoothingFactor = options?.jitterCompensation ?? 0.0;
  const missingDataTolerance = options?.jitterMissingDataTolerance ?? 5;

  // Update state
  jitterState.smoothingFactor = smoothingFactor;
  jitterState.missingDataTolerance = missingDataTolerance;

  // Check if current results are "missing" (no meaningful data)
  // Consider results missing if there's no handsControl or poseControl
  const hasData = results &&
    (results.clench !== undefined ||
      results.wristRotationLeft !== undefined ||
      results.poseAngles !== undefined ||
      results.hands !== undefined);

  if (!hasData) {
    // Data is missing
    jitterState.missingDataCounter++;

    // If we have a last good result and haven't exceeded tolerance, use it
    if (
      jitterState.lastGoodResult &&
      jitterState.missingDataCounter <= missingDataTolerance
    ) {
      // Return last good result with updated timestamp
      const compensated = deepClone(jitterState.lastGoodResult);
      if (results.timestamp !== undefined) {
        compensated.timestamp = results.timestamp;
      }
      if (results.canvasBounds !== undefined) {
        compensated.canvasBounds = results.canvasBounds;
      }
      return compensated;
    }

    // No data and exceeded tolerance, return current (which may be sparse)
    return results;
  }

  // Data exists - reset missing data counter
  jitterState.missingDataCounter = 0;

  // Apply smoothing if we have a previous result and smoothing is enabled
  if (smoothingFactor > 0 && jitterState.lastGoodResult) {
    const smoothed = smoothNumericValues(
      results,
      jitterState.lastGoodResult,
      smoothingFactor,
    );
    // Update last good result
    jitterState.lastGoodResult = deepClone(smoothed);
    return smoothed;
  }

  // No smoothing needed or no previous result - store current as last good
  jitterState.lastGoodResult = deepClone(results);
  return results;
}

// Set up camera and canvas
function onResizeActual(): void {
  // Use actual video dimensions if available, otherwise fall back to resolution option
  let width, height;

  if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
    // Use actual video stream resolution
    width = videoElement.videoWidth;
    height = videoElement.videoHeight;
  } else {
    // Fall back to resolution option if video not loaded yet
    const resolution = globalThis.options?.resolution || "medium";
    switch (resolution) {
      case "low":
        width = 640;
        height = 360;
        break;
      case "medium":
        width = 960;
        height = 540;
        break;
      case "high":
        width = 1280;
        height = 720;
        break;
      default:
        width = 960;
        height = 540;
    }
  }

  // Swap canvas dimensions for 90/270 rotation so rotated video fills canvas
  const rotation = parseInt(globalThis.options?.cameraRotation || "0");
  if (rotation === 90 || rotation === 270) {
    [width, height] = [height, width];
  }

  // Update canvas drawing surface dimensions if needed
  if (canvasElement.width !== width || canvasElement.height !== height) {
    canvasElement.width = width;
    canvasElement.height = height;
    // Don't return early - we still need to calculate canvasBounds
  }

  // Compute CSS "contain" fit within parent container
  // (explicit JS sizing instead of CSS object-fit: contain for cross-browser reliability)
  const container = canvasElement.parentElement!.getBoundingClientRect();
  const scale = Math.min(container.width / width, container.height / height);
  const cssWidth = width * scale;
  const cssHeight = height * scale;
  canvasElement.style.width = `${cssWidth}px`;
  canvasElement.style.height = `${cssHeight}px`;

  // Vertical alignment via CSS top positioning
  const verticalAlignment = globalThis.options?.canvasVerticalAlignment ||
    "bottom";
  if (verticalAlignment === "top") {
    canvasElement.style.top = "0px";
  } else if (verticalAlignment === "center") {
    canvasElement.style.top = `${(container.height - cssHeight) / 2}px`;
  } else {
    // bottom (default)
    canvasElement.style.top = `${container.height - cssHeight}px`;
  }

  // Sync controller overlay with canvas dimensions
  syncControllerOverlayWithCanvas();

  // Canvas bounds — element position IS the rendered bounds since JS handles sizing
  const canvasBoundingBox = canvasElement.getBoundingClientRect();

  const newCanvasBounds = {
    canvasWidth: canvasElement.width,
    canvasHeight: canvasElement.height,
    renderedX: canvasBoundingBox.x,
    renderedY: canvasBoundingBox.y,
    renderedWidth: canvasBoundingBox.width,
    renderedHeight: canvasBoundingBox.height,
  };

  // Track canvas bounds changes
  const newHash = JSON.stringify(newCanvasBounds);
  if (perfMetrics.lastCanvasBoundsHash !== newHash) {
    perfMetrics.canvasBoundsChanges++;
    const canvasBoundingBox = canvasElement.getBoundingClientRect();
    perfMetrics.lastCanvasBoundsHash = newHash;
  }

  canvasBounds = newCanvasBounds;
  // MetapageController.tsx uses this to apply transforms
  globalThis.metaframe?.setOutput("canvasBounds", canvasBounds);
}

function onResize(): void {
  onResizeActual();
  setTimeout(onResizeActual, 50);
}

// update the size of the canvas
const resizeObserver = new ResizeObserver((_) => {
  lastResizeTrigger = "resize-observer";
  onResize();
});
resizeObserver.observe(canvasElement.parentElement!);

// Sync controller overlay to match output-canvas dimensions
function syncControllerOverlayWithCanvas(): void {
  const rect = canvasElement.getBoundingClientRect();
  controllerOverlay.style.width = `${rect.width}px`;
  controllerOverlay.style.height = `${rect.height}px`;
  controllerOverlay.style.top = `${rect.top}px`;
  controllerOverlay.style.left = `${rect.left}px`;
}

// Render overlay controls (hands, pose) - MUST be called while canvas IS mirrored
// These need to align with the mirrored video
function renderOverlayControls(): void {
  if (globalThis.options.renderHandsOverlay && handResults?.landmarks) {
    renderHandsOverlay();
  }
  if (globalThis.options.renderPoseOverlay && poseResults?.landmarks) {
    renderPoseOverlay();
  }
}

// Render widget controls (scalar, rotation, debug) - MUST be called while canvas is NOT mirrored
function renderWidgetControls(): void {
  if (scalarResults) {
    renderScalarControl({
      canvasCtx,
      scalarResults,
      options: globalThis.options,
    });
  }

  // Only render rotation control if enabled in options
  const rotationRenderingEnabled =
    globalThis.options?.rotationRendering !== false;
  if (rotationResults && rotationRenderingEnabled) {
    renderRotationControl({
      canvasCtx,
      rotationResults,
      handsVisible,
    });
  }

  // Only render clench if enabled in options
  if (
    handsControl?.clench &&
    (globalThis.options?.debugClench || globalThis.options?.debugMicrophone)
  ) {
    renderDebugClench(canvasCtx, handsControl?.clench);
  }

  if (globalThis.options.debug) {
    if (canvasBounds) {
      canvasCtx.strokeStyle = "#ff0000";
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeRect(
        1,
        1,
        canvasElement.width - 2,
        canvasElement.height - 2,
      );
    }
    renderDebugOverlay();
  }
}

// Render hands overlay on top of segmented person
function renderHandsOverlay(): void {
  if (!handResults?.landmarks) return;

  const color = globalThis.options?.renderHandsColor || "#00FF00";
  const opacity = globalThis.options?.renderHandsOpacity ?? 1;

  canvasCtx.save();
  canvasCtx.globalAlpha = opacity;
  // Use regular hand landmarks directly (no pose transformation)
  // NOTE: handResults.landmarks are ALREADY MIRRORED in the main loop (line ~1156)
  // so we don't need to mirror them again here
  for (const landmarks of handResults.landmarks) {
    // const mirroredLandmarks = landmarks.map((landmark: any) => ({
    //   x: landmark.x,
    //   y: landmark.y,
    //   z: landmark.z,
    // }));
    // Draw hand connections
    globalThis.drawConnectors(
      canvasCtx,
      landmarks,
      globalThis.HAND_CONNECTIONS,
      {
        color,
        lineWidth: 5,
      },
    );
    // Draw hand landmarks
    globalThis.drawLandmarks(canvasCtx, landmarks, {
      color,
      lineWidth: 2,
    });
  }
  canvasCtx.restore();
}

// Render hands overlay on top of segmented person
function renderPoseOverlay(): void {
  if (!poseResults?.landmarks) return;

  const color = globalThis.options?.renderPoseColor || "#00FF00";
  const opacity = globalThis.options?.renderPoseOpacity ?? 1;

  canvasCtx.save();
  canvasCtx.globalAlpha = opacity;
  // Use regular hand landmarks directly (no pose transformation)
  for (const landmarks of poseResults.landmarks) {
    // This doesn't work if I don't do this, but there's no modification
    // TODO: Figure out why this is necessary
    const mirroredLandmarks = landmarks.map((landmark: any) => ({
      x: landmark.x,
      y: landmark.y,
      z: landmark.z,
    }));

    // Draw hand connections
    globalThis.drawConnectors(
      canvasCtx,
      mirroredLandmarks, //poseResults?.landmarks,
      globalThis.POSE_CONNECTIONS,
      {
        color,
        lineWidth: 5,
      },
    );
    // Draw hand landmarks
    globalThis.drawLandmarks(canvasCtx, poseResults?.landmarks, {
      color,
      lineWidth: 2,
    });
  }
  canvasCtx.restore();
}

// Apply camera rotation transform to canvas context.
// Canvas dimensions are already swapped for 90/270 in onResizeActual().
function applyRotationTransform(ctx: CanvasRenderingContext2D): void {
  const rotation = parseInt(globalThis.options?.cameraRotation || "0");
  if (rotation === 0) return;
  const cw = canvasElement.width;
  const ch = canvasElement.height;
  ctx.translate(cw / 2, ch / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  if (rotation === 90 || rotation === 270) {
    // Canvas dims are swapped, so un-translate by swapped dims
    ctx.translate(-ch / 2, -cw / 2);
  } else {
    ctx.translate(-cw / 2, -ch / 2);
  }
}

// Get the draw dimensions for the video source (original, pre-rotation size).
function getVideoDrawDims(): { w: number; h: number } {
  const rotation = parseInt(globalThis.options?.cameraRotation || "0");
  if (rotation === 90 || rotation === 270) {
    // Video source is in original orientation; canvas is swapped
    return { w: canvasElement.height, h: canvasElement.width };
  }
  return { w: canvasElement.width, h: canvasElement.height };
}

// Render video to canvas (without segmentation)
// This function is used when segmentation is disabled.
function renderVideoToCanvas(): void {
  // Clear canvas
  canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);

  // When the camera is hidden we skip drawing the video frame entirely (the
  // camera still runs so gestures keep working). Overlays still render below.
  if (!globalThis.options?.hideCamera) {
    const { w: drawW, h: drawH } = getVideoDrawDims();

    // Apply horizontal mirroring to the entire canvas (selfie view)
    if (!globalThis.options?.cameraFlipHorizontal) {
      canvasCtx.save();
      canvasCtx.translate(canvasElement.width, 0);
      canvasCtx.scale(-1, 1);
      applyRotationTransform(canvasCtx);
      canvasCtx.drawImage(videoElement, 0, 0, drawW, drawH);
      canvasCtx.restore();
    } else {
      canvasCtx.save();
      applyRotationTransform(canvasCtx);
      canvasCtx.drawImage(videoElement, 0, 0, drawW, drawH);
      canvasCtx.restore();
    }
  }

  // Render overlay controls while canvas is mirrored (to align with video)
  renderOverlayControls();
}

// Initialize segmentation manager
const segmentation = new SegmentationManager({
  canvasElement,
  canvasCtx,
  getVideoDrawDims,
  applyRotationTransform,
  renderOverlayControls,
  renderWidgetControls,
  perfMetrics,
  updateDetectionHistory,
});
__overlayCleanup.segmentation = segmentation;

// Frame rate throttling
let lastFrameTime = 0;

function getFrameInterval(): number {
  const frameRate = globalThis.options?.frameRate || "30";
  if (frameRate === "15") return 1000 / 15; // ~66ms
  if (frameRate === "60" || frameRate === "max") return 1000 / 60; // ~16ms
  return 1000 / 30; // default: 30fps (~33ms)
}

async function predictWebcam(): Promise<void> {
  // Early return if camera is disabled
  const enableCamera = globalThis.options?.enableCamera !== false;
  if (!cameraManager.isRunning || !enableCamera) {
    return;
  }

  const startTimeMs = performance.now();
  const frameStartTime = startTimeMs;

  // Frame rate gate — always enforced, no "max/unlimited" mode.
  const frameInterval = getFrameInterval();
  const timeSinceLastFrame = startTimeMs - lastFrameTime;
  if (timeSinceLastFrame < frameInterval) {
    if (cameraManager.isRunning === true) {
      globalThis.requestAnimationFrame(predictWebcam);
    }
    return;
  }
  lastFrameTime = startTimeMs;
  iostream.reset();

  // Calculate FPS
  if (perfMetrics.frameHistory.length > 0) {
    const avgFrameTime = calculateAverage(perfMetrics.frameHistory);
    perfMetrics.fps = avgFrameTime > 0 ? 1000 / avgFrameTime : 0;
  }

  const flip = !globalThis.options?.cameraFlipHorizontal;

  if (lastFlipState !== flip) {
    lastFlipState = flip;
  }

  // Only run detection AND flip transformations when video frame changes.
  // One createImageBitmap feeds all three models in the unified worker.
  if (lastVideoTime !== videoElement.currentTime) {
    lastVideoTime = videoElement.currentTime;

    const shouldDetectPose = !!(globalThis?.options.emitRawPoseData ||
      globalThis?.options.renderPoseOverlay);
    // Hiding the camera means nothing is drawn, so segmentation is pointless.
    const shouldRunSeg = !!(globalThis?.options.segmentation) &&
      !globalThis?.options.hideCamera;

    const detectionStart = performance.now();
    const visionResult = await visionDetector.detect(
      videoElement,
      startTimeMs,
      { runHand: true, runPose: shouldDetectPose, runSeg: shouldRunSeg },
    );
    const detectionMs = performance.now() - detectionStart;

    // detect() returns undefined when it skipped this frame (busy worker or
    // timeout). Keep the previous results in that case so a transient gap
    // doesn't wipe hand/clench data and reset gesture controllers — only a
    // real detection result updates them.
    if (visionResult) {
      handResults = visionResult.hand;
      poseResults = visionResult.pose;

      // Apply flip transformations (only once per fresh detection).
      if (poseResults && flip) {
        poseResults = visionDetector.applyPoseFlip(poseResults);
      }
      if (handResults && flip) {
        handResults = visionDetector.applyHandFlip(handResults);
      }
    }

    // Hand and pose share the overall detection time; log them separately for
    // the debug overlay (they always run together now).
    perfMetrics.handDetectionTime = detectionMs;
    perfMetrics.poseDetectionTime = detectionMs;
    updateDetectionHistory("hand", detectionMs);
    updateDetectionHistory("pose", detectionMs);

    // Render segmentation immediately when new mask data is available.
    if (visionResult?.segmentation) {
      segmentation.renderResult(
        visionResult.segmentation.maskData,
        visionResult.segmentation.maskWidth,
        visionResult.segmentation.maskHeight,
        videoElement,
      );
    }
  } else if (segmentation.isActive()) {
    // No new video frame but cached mask is still fresh — re-composite.
    segmentation.renderWithCachedMask(videoElement);
  }

  // Stabilize pose results (smoothing + memory).
  if (poseResults) {
    poseResults = visionDetector.stabilizePose(poseResults, startTimeMs);
  }

  // Transform hand landmarks using stabilized pose data.
  let transformedHandResults = handResults;

  // Track post-processing time (clench, angles, controls calculations)
  const postProcessingStart = performance.now();

  poseControl = calculatePoseControl(poseResults);
  handsControl = handResults ? calculateHandsControl(handResults) : undefined;
  const poseAngles: PoseAngles | undefined = poseResults?.worldLandmarks?.[0]
    ? calculatePoseAngles(poseResults?.worldLandmarks?.[0])
    : undefined;

  // Only update scalarResults if scalarController is enabled
  if (scalarController && handResults && handsControl) {
    scalarResults = scalarController.onInputs({
      clench: handsControl.clench,
      hands: handResults as MediapipeHandResults,
      pose: poseResults,
    });
  }

  handsVisible = getHandsVisible(handResults);

  // Only update rotationResults if rotationController is enabled
  if (rotationController && handsControl) {
    rotationResults = rotationController.onInputs({
      clench: handsControl.clench,
      hands: handResults,
      pose: poseResults,
    });
  }

  // Record post-processing time
  perfMetrics.postProcessingTime = performance.now() - postProcessingStart;
  updateDetectionHistory("postProcessing", perfMetrics.postProcessingTime);

  // Track hand detection state changes
  const currentHandCount = handResults?.landmarks?.length || 0;
  perfMetrics.currentHandCount = currentHandCount;

  if (perfMetrics.lastHandCount !== currentHandCount) {
    perfMetrics.handDetectionStateChanges++;
  }
  perfMetrics.lastHandCount = currentHandCount;

  // if external transform coordinates are provided, apply transforms
  const hasScreenCoordinates = !!globalThis?.screenCoordinates;
  const hasCanvasBounds = !!canvasBounds;
  const willApplyTransform = hasScreenCoordinates && hasCanvasBounds;

  // Track transform application state changes
  perfMetrics.lastTransformState = willApplyTransform;

  // Output combined results
  let combinedResults: CombinedResults = {
    ...handsControl,
    ...poseControl,
    poseAngles,
    hands: handResults,
    // handsTransformedWithPose: globalThis?.options.emitRawHandData
    //   ? transformedHandResults
    //   : undefined,
    pose: poseResults,
    timestamp: startTimeMs,
  };
  if (canvasBounds) {
    combinedResults.canvasBounds = canvasBounds;
  }

  // poseSignal.dispatch(stabilizedPoseResults);
  // handsControlSignal.dispatch(handsControl);

  // Apply jitter compensation at the topmost level
  // const compensatedResults = applyJitterCompensation(combinedResults);
  combinedResults = applyJitterCompensation(combinedResults);
  combinedResults = handTokenizerController?.onInputs(combinedResults) ??
    combinedResults;

  // Track output size for performance monitoring
  // perfMetrics.outputSize = JSON.stringify(combinedResults).length;

  // Calculate total computation time
  perfMetrics.totalComputeTime = performance.now() - frameStartTime;

  const resultsForOutput = {
    ...combinedResults,
    hands: globalThis?.options.emitRawHandData
      ? combinedResults.hands
      : undefined,
    pose: globalThis?.options.emitRawPoseData
      ? combinedResults.pose
      : undefined,
  };
  const hashParamId = getHashParamFromWindow("id");
  // console.log(`Controller Metapage Instance ${hashParamId}: 🙌🙌🙌 setOutputs`, resultsForOutput);
  iostream.setOutputs(resultsForOutput);

  handsControl && grabTwistLeftRightController?.onInputs(handsControl);
  handsControl && grabTwistForwardBackwardController?.onInputs(handsControl);
  handsControl && glyphLeftRightController?.onInputs(combinedResults);
  handsControl && glyphForwardBackwardController?.onInputs(combinedResults);
  // handsControl && proto01LeftRightController?.onInputs(combinedResults);
  // handsControl && proto01ForwardBackwardController?.onInputs(combinedResults);
  // handsControl && proto02LeftRightController?.onInputs(combinedResults);
  // handsControl && proto02ForwardBackwardController?.onInputs(combinedResults);

  twoHandGrabOpenCloseController?.onInputs(combinedResults);
  flickHandLeftRightController?.onInputs(combinedResults);
  menuNavigationController?.onInputs(combinedResults);
  iostream.flush();
  // handsControl && twoHandGrabOpenCloseController?.onInputs(handsControl);
  // Render to canvas if segmentation is disabled or stale
  // Segmentation rendering happens inline in the detection block above.
  // Only fall back to plain video render when segmentation is disabled/stale.
  const renderStart = performance.now();
  if (!segmentation.isActive()) {
    renderVideoToCanvas();
    renderWidgetControls();
  }

  perfMetrics.renderTime = performance.now() - renderStart;

  // Update frame history for FPS calculation
  const totalFrameTime = performance.now() - frameStartTime;
  updateFrameHistory(totalFrameTime);
  perfMetrics.frameTime = totalFrameTime;

  // Call this function again to keep predicting when the browser is ready.
  if (cameraManager.isRunning === true) {
    globalThis.requestAnimationFrame(predictWebcam);
  }
}

// Initialize CameraManager
const cameraManager = new CameraManager({
  videoElement,
  canvasElement,
  canvasCtx,
  onFrame: async () => {
    await predictWebcam();
  },
  onResize: (trigger: string) => {
    lastResizeTrigger = trigger;
    onResize();
  },
  getDiagTime,
});
cameraManager.handleHmrRestore();
__overlayCleanup.cameraManager = cameraManager;

// Orientation change handler - DO NOT restart camera, just update canvas bounds
const handleOrientationChange = () => {
  const newOrientation = window.screen?.orientation?.angle ??
    (window as any).orientation ?? 0;

  const wasPortrait = lastOrientation === 0 || lastOrientation === 180;
  const isNowPortrait = newOrientation === 0 || newOrientation === 180;

  lastOrientation = newOrientation;

  if (wasPortrait !== isNowPortrait) {
    lastResizeTrigger = "orientation-change";
    onResize();
  }
};

window.addEventListener("orientationchange", handleOrientationChange);
if (window.screen?.orientation) {
  window.screen.orientation.addEventListener("change", handleOrientationChange);
}

// Call onResize initially
lastResizeTrigger = "initial";
onResize();

// Set up ResizeObserver to keep controller-overlay synchronized with canvas
const canvasResizeObserver = new ResizeObserver(() => {
  syncControllerOverlayWithCanvas();
});
canvasResizeObserver.observe(canvasElement);

// Also observe window resizes
globalThis.addEventListener("resize", syncControllerOverlayWithCanvas);

const applyScalarControlOption = (scalarControlEnabled: boolean) => {
  if (scalarControlEnabled) {
    if (scalarController) {
      return;
    }
    scalarController = generateScalarController({ iostream });
    // Add scalar controller to canvas-wrapper for top-right positioning (if enabled)
    document
      .getElementById("canvas-wrapper")!
      .appendChild((scalarController as unknown as ScalarController).root);
    (scalarController as unknown as ScalarController).root.id =
      "controller-overlay-scalar";
  } else {
    scalarController?.dispose();
    scalarController = null;
  }
};

const applyRotationControlOption = (rotationControlEnabled: boolean) => {
  if (rotationControlEnabled) {
    if (rotationController) {
      return;
    }
    rotationController = generateRotationController({ iostream });
    if (rotationController as unknown as RotationController | null) {
      document
        .getElementById("canvas-wrapper")!
        .appendChild(
          (rotationController as unknown as RotationController).root,
        );
      (rotationController as unknown as RotationController).root.id =
        "controller-overlay-rotation";
    }
  } else {
    rotationController?.dispose();
    rotationController = null;
  }
};

let controllersInitialized = false;

function initControllersOnce() {
  if (controllersInitialized) return;
  controllersInitialized = true;

  const root = document.getElementById("controller-overlay")! as HTMLDivElement;

  handTokenizerController = new HandTokenizerController({ iostream, root });

  glyphLeftRightController = new DirectionalControllerGlyphController(
    { iostream, root },
    { hand: "left", orientation: "horizontal" },
  );

  glyphForwardBackwardController = new DirectionalControllerGlyphController(
    { iostream, root },
    { hand: "right", orientation: "vertical" },
  );
  __overlayCleanup.controllers.push(
    glyphLeftRightController,
    glyphForwardBackwardController,
  );

  // proto01LeftRightController = new DirectionalControllerPrototype01Controller(
  //   { iostream, root },
  //   { hand: "left", orientation: "horizontal" },
  // );

  // proto01ForwardBackwardController =
  //   new DirectionalControllerPrototype01Controller(
  //     { iostream, root },
  //     { hand: "right", orientation: "vertical" },
  //   );

  // proto02LeftRightController = new DirectionalControllerPrototype02Controller(
  //   { iostream, root },
  //   { hand: "left", orientation: "horizontal" },
  // );

  // proto02ForwardBackwardController =
  //   new DirectionalControllerPrototype02Controller(
  //     { iostream, root },
  //     { hand: "right", orientation: "vertical" },
  //   );

  twoHandGrabOpenCloseController = new TwoHandGrabOpenCloseController({
    iostream,
    root,
  });
}

const applyOptions = (trigger = "unknown") => {
  console.log(
    `⚙️ [camera-instance:${_instanceId}] applyOptions(trigger=${trigger}) camera.state=${cameraManager.state} enableCamera=${!!globalThis
      .options?.enableCamera} seg=${!!globalThis.options?.segmentation}`,
  );

  segmentation.applyOptions({
    // Hiding the camera draws nothing, so segmentation has nothing to composite.
    segmentation: (globalThis.options?.segmentation ?? false) &&
      !globalThis.options?.hideCamera,
    cameraFlipHorizontal: globalThis.options?.cameraFlipHorizontal ?? false,
  });

  applyScalarControlOption(globalThis.options?.scalarControl);
  applyRotationControlOption(globalThis.options?.rotationControl);

  // Controllers are created once — their constructor params (metaframe, root,
  // hand, orientation) never change. They receive per-frame data via onInputs().
  initControllersOnce();

  // Update transcriber with current microphone options
  transcriber.applyOptions(globalThis.options);

  cameraManager.applyOptions({
    enableCamera: !!globalThis.options?.enableCamera,
  });
};

// Listen for options changes
const optionsManager = new OptionsManager();
optionsManager.onChange((info) => {
  if (info.resolutionChanged || info.rotationChanged) {
    lastResizeTrigger = "options-resolution-change";
    onResize();
  }
  applyOptions("options-changed");
});
optionsManager.startListening();

applyOptions("initial");

// Cleanup on page unload or HMR
// Handle HMR cleanup (if using Vite or similar)
// @ts-ignore
// if (import.meta.hot) {
//   // @ts-ignore
//   import.meta.hot.dispose(() => {
//     cameraManager.cleanup();
//   });
// }

// Detect page unloads — log why so we can find what triggers reloads
if (typeof document !== "undefined" && document.defaultView) {
  document.defaultView.addEventListener("beforeunload", () => {
    console.warn(
      "🚨 PAGE UNLOADING — this will destroy all models and camera",
      {
        moduleLoadCount: _moduleLoadCount,
        uptime: getDiagTime() + "s",
        trigger: new Error("beforeunload stack trace"),
      },
    );
    // cameraManager.cleanup();
  });
}
